import {
  ArrowRightIcon,
  DotsThreeIcon,
  ListBulletsIcon,
} from '@phosphor-icons/react';
import {
  Alert,
  Badge,
  Button,
  Cell,
  Column,
  EmptyState,
  Menu,
  Pagination,
  Row,
  SegmentedTabs,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import { useState } from 'react';
import { useListingMapping } from '../hooks/use-listing-mapping';
import { useMappableListings } from '../hooks/use-mappable-listings';
import { collapseRepeatedLead } from '../lib/connection-label';
import { mappingErrorMessage } from '../lib/mapping-errors';
import {
  ALL_SOURCES,
  EXCLUDED_REASON,
  excludeBody,
  mapBody,
  type SourceFilter,
} from '../lib/mappable-params';
import type {
  ListingMappingBody,
  ListingMappingResult,
  ListingRef,
  MappableListing,
  MappingState,
} from '../lib/types';
import { CreateItemModal } from './CreateItemModal';
import { ExcludeListingModal } from './ExcludeListingModal';
import {
  MappingActions,
  type MappingFilter,
  showInactiveFilter,
} from './MappingActions';
import { PostgresItemPickerModal } from './PostgresItemPickerModal';
import { GatedButton } from './v2/GatedButton';

export type MappableListingsTableProps = {
  state: MappingState;
  /** The page behind each tab; the tabs hand the choice back. */
  onStateChange: (state: MappingState) => void;
  initialSearch?: string;
  /** The plan or role can't change listings: actions show, disabled. */
  isReadOnly?: boolean;
  /** The plan hides these actions altogether. */
  isActionsHidden?: boolean;
  /** For a page that has its own tabs for the two states. */
  hideStateTabs?: boolean;
  /** Offers "Undo mapping" in a mapped row's menu. */
  onUndo?: (listing: MappableListing) => void;
  /** An undo is on its way: the menu's Undo mapping waits. */
  isUndoing?: boolean;
};

export const STORAGE_PROVIDER_LABEL = 'Warehouse';

const LOADING_ROWS = 7;
const COLUMN_COUNT = 3;

const STATE_TABS: { id: MappingState; label: string }[] = [
  { id: 'unmapped', label: 'Unmapped' },
  { id: 'mapped', label: 'Mapped' },
];

export const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: ALL_SOURCES, label: 'All sources' },
  { value: 'marketplace', label: 'Marketplaces' },
  { value: 'storage_provider', label: 'Warehouses' },
];

const EMPTY_STATES: Record<
  MappingState,
  { title: string; description: string }
> = {
  unmapped: {
    title: 'No unmapped listings',
    description: 'Every listing in this view is mapped to a catalog item.',
  },
  mapped: {
    title: 'No mapped listings',
    description: 'Listings mapped to a catalog item show here.',
  },
};

/* Why a listing has no item yet, in words; an unknown reason shows as the API sent it. */
const UNMAPPED_REASONS: Record<string, string> = {
  no_candidate: 'No matching item',
  ambiguous: 'Several matching items',
  no_identifiers: 'No identifiers',
  gtin_conflict: 'GTIN conflict',
  pack_unknown: 'Pack size unknown',
  [EXCLUDED_REASON]: 'Excluded',
};

const SAVED_MESSAGES: Record<ListingMappingResult['status'], string> = {
  mapped: 'Listing mapped successfully',
  unchanged: 'Listing was already mapped to that item',
  excluded: 'Listing excluded successfully',
};

/* An excluded listing is unmapped on purpose; it is mapped again, not made into an item. */
const canCreateItem = (listing: MappableListing) =>
  listing.state === 'unmapped' && listing.unmappedReason !== EXCLUDED_REASON;

/* One line that ends in an ellipsis instead of running into the next cell; the full text is the tooltip. */
export function TruncatedLine({
  text,
  className,
}: {
  text: string;
  className: string;
}) {
  return (
    <span className={`sk:block sk:truncate ${className}`} title={text}>
      {text}
    </span>
  );
}

export function ListingCell({
  listing,
}: {
  listing: Omit<MappableListing, 'itemTitle'>;
}) {
  return (
    <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
        <TruncatedLine
          text={collapseRepeatedLead(
            listing.connectionLabel ?? 'Unknown connection',
          )}
          className="sk:min-w-0"
        />
        {listing.sourceKind === 'storage_provider' ? (
          <Badge tone="info">{STORAGE_PROVIDER_LABEL}</Badge>
        ) : null}
      </div>
      {listing.sku ? (
        <TruncatedLine
          text={listing.sku}
          className="sk:text-sm sk:font-medium sk:text-fg"
        />
      ) : null}
      {listing.title ? (
        <TruncatedLine text={listing.title} className="sk:text-sm sk:text-fg" />
      ) : null}
      {[
        ['GTIN', listing.gtin],
        ['ASIN', listing.asin],
      ].map(([label, value]) =>
        value ? (
          <TruncatedLine
            key={label}
            text={`${label} ${value}`}
            className="sk:text-dense sk:text-fg-secondary"
          />
        ) : null,
      )}
    </div>
  );
}

function StateCell({ listing }: { listing: MappableListing }) {
  if (listing.state === 'mapped') {
    return listing.itemTitle ? (
      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
        <ArrowRightIcon
          size={14}
          aria-hidden
          className="sk:shrink-0 sk:text-fg-tertiary"
        />
        <TruncatedLine text={listing.itemTitle} className="sk:text-sm" />
      </div>
    ) : null;
  }

  const reason = listing.unmappedReason;
  const label = UNMAPPED_REASONS[reason ?? ''] ?? reason ?? 'Unmapped';

  return (
    <div className="sk:min-w-0 sk:truncate" title={label}>
      <Badge tone={reason === EXCLUDED_REASON ? 'neutral' : 'caution'}>
        {label}
      </Badge>
    </div>
  );
}

const UNDO_ACTION = 'undo';
const EXCLUDE_ACTION = 'exclude';

export type MappedRowActionsProps = {
  isReadOnly?: boolean;
  /** A save is on its way: nothing else starts. */
  isBusy: boolean;
  isUndoing?: boolean;
  onRemap: () => void;
  onExclude: () => void;
  onUndo?: () => void;
};

/* A mapped row stays quiet: a ghost Re-map, and Undo mapping / Exclude behind "...". */
export function MappedRowActions({
  isReadOnly,
  isBusy,
  isUndoing,
  onRemap,
  onExclude,
  onUndo,
}: MappedRowActionsProps) {
  const more = (
    <GatedButton
      variant="ghost"
      size="sm"
      aria-label="More actions"
      isReadOnly={isReadOnly}
      isDisabled={isBusy}
    >
      <DotsThreeIcon size={16} weight="bold" aria-hidden />
    </GatedButton>
  );

  return (
    <>
      <GatedButton
        variant="ghost"
        size="sm"
        isReadOnly={isReadOnly}
        isDisabled={isBusy}
        onPress={onRemap}
      >
        Re-map
      </GatedButton>
      {/* Read-only: the disabled trigger with its reason, no menu to open. */}
      {isReadOnly ? (
        more
      ) : (
        <Menu
          aria-label="Mapping actions"
          trigger={more}
          actions={[
            ...(onUndo
              ? [
                  {
                    id: UNDO_ACTION,
                    label: 'Undo mapping',
                    isDisabled: isUndoing,
                  },
                ]
              : []),
            { id: EXCLUDE_ACTION, label: 'Exclude' },
          ]}
          onAction={id => (id === UNDO_ACTION ? onUndo?.() : onExclude())}
        />
      )}
    </>
  );
}

/* The Source filter of a mappable list (M2: the Mapped tab's two views). */
export const sourceFilter = (
  value: SourceFilter,
  onChange: (source: SourceFilter) => void,
): MappingFilter => ({
  id: 'source',
  label: 'Source',
  value,
  options: SOURCE_OPTIONS,
  onChange: source => onChange(source as SourceFilter),
});

/* Pulsing rows while a page loads, one cell across the table's columns. */
export const placeholderRows = (columnCount: number) =>
  Array.from({ length: LOADING_ROWS }, (_, row) => (
    <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
      <Cell colSpan={columnCount}>
        <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      </Cell>
    </Row>
  ));

type PageFilter = { page: number; limit: number };

/* The pager under a mappable list; a page change scrolls back to the top. */
export function ListPagination({
  filter,
  total,
  onChange,
}: {
  filter: PageFilter;
  total: number;
  onChange: <K extends keyof PageFilter>(key: K, value: PageFilter[K]) => void;
}) {
  return total > 0 ? (
    <Pagination
      page={filter.page}
      pageSize={filter.limit}
      total={total}
      onPageChange={page => {
        onChange('page', page);
        window.scrollTo({ top: 0 });
      }}
      onPageSizeChange={limit => onChange('limit', limit)}
    />
  ) : null;
}

/*
 * Map, re-map and exclude one listing: the item picker and the exclude
 * confirmation, each saving with its toast. `modals` goes in the page once.
 */
export function useListingEdits() {
  const mapping = useListingMapping();
  const [picking, setPicking] = useState<ListingRef | null>(null);
  const [excluding, setExcluding] = useState<ListingRef | null>(null);

  const save = async (listing: ListingRef, body: ListingMappingBody) => {
    try {
      const result = await mapping.mutateAsync({ listingId: listing.id, body });
      ToastMessage.success(SAVED_MESSAGES[result.status]);
      setPicking(null);
      setExcluding(null);
    } catch (failure) {
      ToastMessage.error(mappingErrorMessage(failure));
    }
  };

  const modals = (
    <>
      <PostgresItemPickerModal
        listing={picking}
        onClose={() => setPicking(null)}
        onSelect={itemId => picking && save(picking, mapBody(picking, itemId))}
        isProcessing={mapping.isPending}
      />
      <ExcludeListingModal
        listing={excluding}
        onClose={() => setExcluding(null)}
        onConfirm={() => excluding && save(excluding, excludeBody)}
        isProcessing={mapping.isPending}
      />
    </>
  );

  return {
    isSaving: mapping.isPending,
    pick: setPicking,
    exclude: setExcluding,
    modals,
  };
}

/**
 * Listings of every connection of the account, marketplaces and storage
 * providers alike, to map to a catalog item, re-map or exclude. Reads and
 * writes Postgres only.
 */
export function MappableListingsTable({
  state,
  onStateChange,
  initialSearch,
  isReadOnly,
  isActionsHidden,
  hideStateTabs,
  onUndo,
  isUndoing,
}: MappableListingsTableProps) {
  const { data, loading, error, totalData, filter, filterChanged } =
    useMappableListings(state, initialSearch);
  const edits = useListingEdits();
  const [creating, setCreating] = useState<MappableListing | null>(null);

  const locked = isReadOnly || edits.isSaving;
  const empty = EMPTY_STATES[state];

  return (
    <div className="sk:flex sk:flex-col sk:gap-3">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-3">
        {hideStateTabs ? null : (
          <SegmentedTabs
            label="Mapping state"
            items={STATE_TABS}
            selectedKey={state}
            onSelectionChange={key => onStateChange(key as MappingState)}
          />
        )}
        <MappingActions
          filters={[
            sourceFilter(filter.source, source =>
              filterChanged('source', source),
            ),
            /* Only unmapped listings are left out by status (M2). */
            ...(state === 'unmapped'
              ? [
                  showInactiveFilter(filter.showInactive, isOn =>
                    filterChanged('showInactive', isOn),
                  ),
                ]
              : []),
          ]}
          search={filter.search}
          onSearch={search => filterChanged('search', search)}
        />
      </div>
      {error ? <Alert tone="danger">{mappingErrorMessage(error)}</Alert> : null}
      <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
        <Table aria-label="Listings" className="sk:table-fixed">
          <TableHeader>
            <Column id="listing" isRowHeader className="sk:w-[40%]">
              Listing
            </Column>
            <Column id="state">
              {state === 'mapped' ? 'Linked product' : 'State'}
            </Column>
            <Column
              id="actions"
              aria-label="Actions"
              className={state === 'mapped' ? 'sk:w-[10rem]' : 'sk:w-[17rem]'}
            >
              {''}
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <EmptyState
                icon={<ListBulletsIcon size={20} />}
                title={empty.title}
                description={empty.description}
              />
            )}
          >
            {loading
              ? placeholderRows(COLUMN_COUNT)
              : data.map(listing => (
                  <Row key={listing.id} id={listing.id}>
                    <Cell>
                      <ListingCell listing={listing} />
                    </Cell>
                    <Cell>
                      <StateCell listing={listing} />
                    </Cell>
                    <Cell>
                      {isActionsHidden ? null : (
                        <div className="sk:flex sk:shrink-0 sk:flex-nowrap sk:items-center sk:justify-end sk:gap-2 sk:whitespace-nowrap">
                          {listing.state === 'mapped' ? (
                            <MappedRowActions
                              isReadOnly={isReadOnly}
                              isBusy={edits.isSaving}
                              isUndoing={isUndoing}
                              onRemap={() => edits.pick(listing)}
                              onExclude={() => edits.exclude(listing)}
                              onUndo={onUndo && (() => onUndo(listing))}
                            />
                          ) : (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                isDisabled={locked}
                                onPress={() => edits.pick(listing)}
                              >
                                Map
                              </Button>
                              {canCreateItem(listing) && !isReadOnly ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  isDisabled={locked}
                                  onPress={() => setCreating(listing)}
                                >
                                  Create item
                                </Button>
                              ) : null}
                              {listing.unmappedReason ===
                              EXCLUDED_REASON ? null : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  isDisabled={locked}
                                  onPress={() => edits.exclude(listing)}
                                >
                                  Exclude
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </Cell>
                  </Row>
                ))}
          </TableBody>
        </Table>
        <ListPagination
          filter={filter}
          total={totalData}
          onChange={filterChanged}
        />
      </TableContainer>
      {edits.modals}
      <CreateItemModal listing={creating} onClose={() => setCreating(null)} />
    </div>
  );
}
